/**
 * Football / FlagFootball game-stat entry sections.
 *
 * Lives here rather than inside FootballGameStatsModal so the key list can be
 * asserted against SPORT_STAT_KEYS without importing React or the Supabase
 * client. Every key emitted here MUST be declared in
 * SPORT_STAT_KEYS.Football (and, unless `footballOnly`, in
 * SPORT_STAT_KEYS.FlagFootball) or the player-card stat legend falls back to
 * showing the raw key. See src/data/__tests__/footballStatKeys.test.ts.
 */

export interface StatDef {
  key: string;
  label: string;
  step?: number;
  /** Excluded from FlagFootball, which has no sacks, kicking or fumble recoveries. */
  footballOnly?: boolean;
}

export interface SectionDef {
  id: string;
  label: string;
  border: string;
  bg: string;
  dot: string;
  text: string;
  stats: StatDef[];
}

export const FOOTBALL_STAT_SECTIONS: SectionDef[] = [
  {
    id: 'passing',
    label: 'Passing',
    border: 'border-blue-200',
    bg: 'bg-blue-50/40',
    dot: 'bg-blue-500',
    text: 'text-blue-900',
    stats: [
      { key: 'PassComp', label: 'Comp' },
      { key: 'PassAtt', label: 'Att' },
      { key: 'PassYds', label: 'Yards' },
      { key: 'PassTD', label: 'TDs' },
      { key: 'PassInt', label: 'INTs' },
      { key: 'Pass20Plus', label: '20+ Yds' },
      { key: 'Pass30Plus', label: '30+ Yds' },
      { key: 'Pass40Plus', label: '40+ Yds' },
    ],
  },
  {
    id: 'rushing',
    label: 'Rushing',
    border: 'border-green-200',
    bg: 'bg-green-50/40',
    dot: 'bg-green-500',
    text: 'text-green-900',
    stats: [
      { key: 'RushAtt', label: 'Carries' },
      { key: 'RushYds', label: 'Yards' },
      { key: 'RushTD', label: 'TDs' },
      { key: 'RushFumble', label: 'Fumbles' },
      { key: 'BrokenTkl', label: 'Broken Tkls' },
      { key: 'Rush20Plus', label: '20+ Yds' },
      { key: 'Rush30Plus', label: '30+ Yds' },
      { key: 'Rush40Plus', label: '40+ Yds' },
    ],
  },
  {
    id: 'receiving',
    label: 'Receiving',
    border: 'border-amber-200',
    bg: 'bg-amber-50/40',
    dot: 'bg-amber-500',
    text: 'text-amber-900',
    stats: [
      { key: 'Targets', label: 'Targets' },
      { key: 'Rec', label: 'Receptions' },
      { key: 'RecYds', label: 'Yards' },
      { key: 'RecTD', label: 'TDs' },
      { key: 'YAC', label: 'YAC' },
      { key: 'RecFumble', label: 'Fumbles' },
      { key: 'Rec20Plus', label: '20+ Yds' },
      { key: 'Rec30Plus', label: '30+ Yds' },
      { key: 'Rec40Plus', label: '40+ Yds' },
    ],
  },
  {
    id: 'defense',
    label: 'Defense',
    border: 'border-red-200',
    bg: 'bg-red-50/40',
    dot: 'bg-red-500',
    text: 'text-red-900',
    stats: [
      { key: 'Tackles', label: 'Tackles' },
      { key: 'TFL', label: 'TFL' },
      { key: 'Sacks', label: 'Sacks', footballOnly: true },
      { key: 'PassDef', label: 'PD' },
      { key: 'Int', label: 'INTs' },
      { key: 'FF', label: 'FF' },
      { key: 'FumbleRec', label: 'FR', footballOnly: true },
    ],
  },
  {
    id: 'special',
    label: 'Special Teams',
    border: 'border-yellow-200',
    bg: 'bg-yellow-50/40',
    dot: 'bg-yellow-500',
    text: 'text-yellow-900',
    stats: [
      { key: 'FG', label: 'FG', footballOnly: true },
      { key: 'XP', label: 'XP', footballOnly: true },
      { key: 'Fumbles', label: 'Fumbles' },
    ],
  },
];

/** Every stat key the football entry modal can write. */
export function getFootballStatKeys(includeFootballOnly: boolean): string[] {
  const keys = FOOTBALL_STAT_SECTIONS.flatMap((section) =>
    section.stats
      .filter((stat) => includeFootballOnly || !stat.footballOnly)
      .map((stat) => stat.key)
  );
  return [...new Set(keys)];
}
